// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pick, isEqual } from 'lodash/fp';

// Utils
import { hasChanged } from '../../utils/react/propsComparisons';
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';

// Actions
import { clearElementFilter, setElementFilter } from './elementFilterActions';
import { getElementFilterSelector } from './elementFilterSelector';
import { clearFilter } from '../../reducers/navigationActions';

// Constants
import { ELEMENT_QUERY_PARAMS_ARRAY } from './elementFilterConstants';
import { APP_QUERY_PARAMS } from '../../../common/utils/urlConstants';

const queryElementFilterSelector = createShallowSelector(
    (state, ownProps) => ownProps.location,
    (location) => {
        const query = pick(ELEMENT_QUERY_PARAMS_ARRAY, location.query);
        if (!Object.keys(query).length) return null;

        const elementFilter = {};

        if (query[APP_QUERY_PARAMS.FILTER_IDS]) elementFilter.ids = query[APP_QUERY_PARAMS.FILTER_IDS].split(',');
        if (query[APP_QUERY_PARAMS.FILTER_QUERY]) elementFilter.query = query[APP_QUERY_PARAMS.FILTER_QUERY];
        if (query[APP_QUERY_PARAMS.FILTER_CONTEXT]) elementFilter.context = query[APP_QUERY_PARAMS.FILTER_CONTEXT];

        return elementFilter;
    },
);

const mapStateToProps = createStructuredSelector({
    storeElementFilter: getElementFilterSelector,
    queryElementFilter: queryElementFilterSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSetElementFilter: (elementFilter) => dispatch(setElementFilter(elementFilter)),
    dispatchClearElementFilter: () => dispatch(clearElementFilter()),
    dispatchClearQueryFilter: () => dispatch(clearFilter()),
});

const queryElementFilterHasChanged = hasChanged('queryElementFilter');
const storeElementFilterHasChanged = hasChanged('storeElementFilter');

/**
 * This component watches the query string to build the appropriate element filter state.
 * It also clears the query string when the stored element filter is changed (but does not update the query string
 * otherwise).
 */
@connect(mapStateToProps, mapDispatchToProps)
class UrlQueryElementFilterSynchroniser extends React.Component {
    componentDidMount() {
        this.syncQueryToStoreElementFilter();
    }

    componentDidUpdate(prevProps) {
        if (queryElementFilterHasChanged(prevProps, this.props)) {
            this.syncQueryToStoreElementFilter();
        }

        if (storeElementFilterHasChanged(prevProps, this.props)) {
            this.syncClearedStoreElementFilterToQuery();
        }
    }

    syncQueryToStoreElementFilter = () => {
        const { queryElementFilter } = this.props;

        return queryElementFilter ? this.updateStoreElementFilter() : this.clearStoreElementFilter();
    };

    updateStoreElementFilter = () => {
        const { queryElementFilter, storeElementFilter, dispatchSetElementFilter } = this.props;

        const currentStoreElementFilter = storeElementFilter && storeElementFilter.toJS();

        if (isEqual(queryElementFilter, currentStoreElementFilter)) return;

        dispatchSetElementFilter(queryElementFilter);
    };

    clearStoreElementFilter = () => {
        const { storeElementFilter, dispatchClearElementFilter } = this.props;

        // Already cleared, so do nothing
        if (!storeElementFilter || storeElementFilter.isEmpty()) return;

        dispatchClearElementFilter();
    };

    syncClearedStoreElementFilterToQuery = () => {
        const { storeElementFilter, dispatchClearQueryFilter } = this.props;
        if (!storeElementFilter || storeElementFilter.isEmpty()) {
            dispatchClearQueryFilter();
        }
    };

    render() {
        return null;
    }
}

UrlQueryElementFilterSynchroniser.propTypes = {
    storeElementFilter: PropTypes.object,
    queryElementFilter: PropTypes.object,
    dispatchSetElementFilter: PropTypes.func,
    dispatchClearElementFilter: PropTypes.func,
    dispatchClearQueryFilter: PropTypes.func,
};

export default UrlQueryElementFilterSynchroniser;
